import { Link } from 'react-router-dom';
import { Bookmark } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDynamicTranslate } from '@/hooks/useDynamicTranslate';

function useArticleText(article) {
  const { i18n } = useTranslation();
  const titleResolved = article?.title_translations?.[i18n.language] || article?.title_hr || '';
  const title = useDynamicTranslate(titleResolved);
  const catName = useDynamicTranslate(article?.category?.name_hr || '');
  return { title, catName };
}

function formatHRDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const m = ['SIJEČNJA','VELJAČE','OŽUJKA','TRAVNJA','SVIBNJA','LIPNJA','SRPNJA','KOLOVOZA','RUJNA','LISTOPADA','STUDENOG','PROSINCA'];
  return `${d.getDate()}. ${m[d.getMonth()]} ${d.getFullYear()}.`;
}

const CATEGORY_STYLES = {
  'hrvatska-vijesti':          { bg: 'bg-indigo-500', text: 'text-white' },
  'varazdinske-vijesti':       { bg: 'bg-emerald-500', text: 'text-white' },
  'kultura':                   { bg: 'bg-pink-500', text: 'text-white' },
  'sport':                     { bg: 'bg-orange-500', text: 'text-white' },
  'turizam-u-medimurju':       { bg: 'bg-teal-500', text: 'text-white' },
  'crna-kronika':              { bg: 'bg-red-800', text: 'text-white' },
  'obrazovanje':               { bg: 'bg-purple-500', text: 'text-white' },
  'gastronomija':              { bg: 'bg-amber-500', text: 'text-white' },
  'zdravstvo':                 { bg: 'bg-cyan-500', text: 'text-white' },
  'lokalna-dogadanja-u-medimurju': { bg: 'bg-lime-500', text: 'text-white' },
};

function CategoryBadge({ slug, name, className = '' }) {
  const style = CATEGORY_STYLES[slug] || { bg: 'bg-gray-600', text: 'text-white' };
  return (
    <span className={`inline-block ${style.bg} ${style.text} text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${className}`}>
      {name}
    </span>
  );
}

// Big hero card — image background, category top-left, title+date bottom, bookmark top-right
function BigHeroCard({ article, height = 'h-[500px]' }) {
  const { t } = useTranslation();
  const { title, catName } = useArticleText(article);
  if (!article) return null;
  return (
    <Link to={`/clanak/${article.slug}`} className={`relative block ${height} rounded-md overflow-hidden group`} data-testid="hero-big">
      <img src={article.cover_image_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=900'} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      {article.is_breaking && (
        <div className="absolute inset-0 bg-red-600 opacity-50 pointer-events-none" data-testid="breaking-red-overlay" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
      {article.is_breaking && (
        <span className="absolute top-4 right-4 inline-block bg-red-600 text-white text-[11px] font-extrabold uppercase tracking-wider px-2 py-1 rounded shadow-lg animate-pulse z-10">{t('breaking.label')}</span>
      )}
      {article.is_pinned && !article.is_breaking && (
        <span className="absolute top-4 right-4 inline-flex items-center gap-1 bg-amber-500 text-zinc-900 text-[11px] font-extrabold uppercase tracking-wider px-2 py-1 rounded shadow-lg z-10" data-testid="hero-pinned-badge">
          <span className="text-[12px] leading-none">★</span> Istaknuto
        </span>
      )}
      {article.category && (
        <div className="absolute top-4 left-4 z-10">
          <CategoryBadge slug={article.category.slug} name={catName || article.category.name_hr} />
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-6 text-white z-10">
        <h2 className="text-2xl md:text-3xl font-bold leading-tight mb-2 line-clamp-3">{title}</h2>
        <p className="text-xs text-white/80 uppercase tracking-wide">{formatHRDate(article.published_at || article.created_at)}</p>
      </div>
    </Link>
  );
}

// Medium card with image
function MediumCard({ article, height = 'h-52' }) {
  const { t } = useTranslation();
  const { title, catName } = useArticleText(article);
  if (!article) return null;
  const showRedFallback = !article.cover_image_url && article.is_breaking;
  return (
    <Link to={`/clanak/${article.slug}`} className={`relative block ${height} rounded-md overflow-hidden group`} data-testid={`hero-medium-${article.id}`}>
      {showRedFallback ? (
        <div className="absolute inset-0 bg-red-600 opacity-50" data-testid="breaking-red-fallback" />
      ) : (
        <img src={article.cover_image_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600'} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      )}
      {article.is_breaking && !showRedFallback && (
        <div className="absolute inset-0 bg-red-600 opacity-50 pointer-events-none" data-testid="breaking-red-overlay" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
      {article.is_breaking && (
        <span className="absolute top-2 right-2 inline-block bg-red-600 text-white text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded shadow animate-pulse z-10">{t('breaking.label')}</span>
      )}
      {article.is_pinned && !article.is_breaking && (
        <span className="absolute top-2 right-2 inline-flex items-center gap-0.5 bg-amber-500 text-zinc-900 text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded shadow z-10" data-testid="hero-pinned-badge-medium">
          ★ Istaknuto
        </span>
      )}
      {article.category && (
        <div className="absolute top-3 left-3 z-10">
          <CategoryBadge slug={article.category.slug} name={catName || article.category.name_hr} />
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-4 text-white z-10">
        <h3 className="text-base md:text-lg font-bold leading-tight mb-1 line-clamp-3">{title}</h3>
        <p className="text-[10px] text-white/80 uppercase tracking-wide">{formatHRDate(article.published_at || article.created_at)}</p>
      </div>
    </Link>
  );
}

// Red highlight card (no image)
function RedHighlightCard({ article }) {
  const { t } = useTranslation();
  const { title } = useArticleText(article);
  if (!article) return null;
  return (
    <Link to={`/clanak/${article.slug}`} className="relative block h-52 rounded-md overflow-hidden bg-red-600 hover:bg-red-700 transition-colors" data-testid="hero-red">
      <div className="absolute top-3 left-3">
        <span className="inline-block bg-white dark:bg-[#0b1220] text-red-700 text-[10px] font-bold uppercase tracking-wider px-2 py-1">{t('home.udarno')}</span>
      </div>
      <button className="absolute top-3 right-3 p-1.5 text-white/70 hover:text-white">
        <Bookmark size={14} />
      </button>
      <div className="absolute inset-0 flex items-end p-4 text-white">
        <div>
          <h3 className="text-base md:text-lg font-bold leading-tight line-clamp-4 mb-1">{title}</h3>
          <p className="text-[10px] text-white/80 uppercase tracking-wide">{formatHRDate(article.published_at || article.created_at)}</p>
        </div>
      </div>
    </Link>
  );
}

// Compact list item with thumbnail
function CompactItem({ article }) {
  const { title, catName } = useArticleText(article);
  if (!article) return null;
  const showRedFallback = !article.cover_image_url && article.is_breaking;
  return (
    <Link to={`/clanak/${article.slug}`} className="block group py-2.5 border-b border-gray-100 dark:border-gray-800 last:border-0" data-testid={`hero-compact-${article.id}`}>
      <div className="flex gap-3">
        <div className="w-20 h-16 shrink-0 rounded-md overflow-hidden bg-gray-100 dark:bg-[#111827]">
          {showRedFallback ? (
            <div className="w-full h-full bg-red-600 opacity-50" data-testid="breaking-red-fallback" />
          ) : (
            <img src={article.cover_image_url || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=160'} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          )}
        </div>
        <div className="min-w-0">
          {article.category && (
            <CategoryBadge slug={article.category.slug} name={catName || article.category.name_hr} className="mb-1" />
          )}
          <h4 className="text-[12px] font-bold text-gray-900 dark:text-gray-100 leading-snug line-clamp-2 group-hover:text-red-600">{title}</h4>
        </div>
      </div>
    </Link>
  );
}

export default function HomeHero({ articles }) {
  if (!articles || articles.length === 0) return null;
  // Pinned articles always rank first (most-recently-pinned wins ties).
  // After that, sort by published date (newest first) so the hero reflects
  // the latest news regardless of caller-side ordering.
  const sorted = [...articles].sort((a, b) => {
    if (!!a.is_pinned !== !!b.is_pinned) return a.is_pinned ? -1 : 1;
    if (a.is_pinned && b.is_pinned) {
      const pa = new Date(a.pinned_at || 0).getTime();
      const pb = new Date(b.pinned_at || 0).getTime();
      if (pa !== pb) return pb - pa;
    }
    const ta = new Date(a.published_at || a.publish_at || a.created_at || 0).getTime();
    const tb = new Date(b.published_at || b.publish_at || b.created_at || 0).getTime();
    return tb - ta;
  });
  // Pick: 1 hero, 2 small mediums (below hero), 2 medium-with-image, 1 red (only when breaking),
  //       6 compact list items in the right column (sidebar news strip).
  const hero = sorted[0];
  const leftSmalls = sorted.slice(1, 3);
  const midMediums = sorted.slice(3, 5);
  const redArticle = sorted.find(a => a.is_breaking);
  // 6 compact items in the right column. If a breaking-red card is shown
  // in the middle column, start the right strip from index 5 (skipping the
  // breaking article duplication); otherwise start at 5 too — middle col only
  // takes the 2 mid mediums in that case.
  const compactStart = 5;
  const compact = sorted.slice(compactStart, compactStart + 6);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start" data-testid="home-hero">
      {/* Left col — big + 2 small below */}
      <div className="lg:col-span-6 space-y-4">
        <BigHeroCard article={hero} />
        <div className="grid grid-cols-2 gap-4">
          {leftSmalls.map(a => <MediumCard key={a.id} article={a} height="h-44" />)}
        </div>
      </div>

      {/* Middle col — 2 mediums + (only if breaking) 1 red */}
      <div className="lg:col-span-3 space-y-4">
        {midMediums.map(a => <MediumCard key={a.id} article={a} height="h-52" />)}
        {redArticle && <RedHighlightCard article={redArticle} />}
      </div>

      {/* Right col — 6 compact items (latest published, newest first) */}
      <div className="lg:col-span-3 bg-white dark:bg-[#0f172a] rounded-md border border-gray-100 dark:border-gray-800 px-4 self-start" data-testid="home-hero-side">
        {compact.map(a => <CompactItem key={a.id} article={a} />)}
      </div>
    </div>
  );
}
